/**
 * Shared picker options + small helpers for SEND (Special Educational Needs &
 * Disabilities) and dietary requirements.
 *
 * Why is this in /utils?
 *   These constants are used by:
 *     - the Parent Quick Add/Edit Child modal  (ParentDashboard.js)
 *     - the Enrollment Wizard medical step     (EnrollmentWizard.js)
 *     - the Child Profile overview section     (ChildProfile.js)
 *     - any staff-facing roster/badge surface  (e.g. AdminDashboard live
 *       ratios)
 *   Keeping them here means the same chip labels appear everywhere and a
 *   future change (e.g. adding "Pescatarian") only needs one edit.
 */

// Common UK childcare dietary requirements. The backend stores this as a
// free List[str] so settings can extend it later.
export const DIETARY_OPTIONS = [
  'Halal',
  'Kosher',
  'Vegetarian',
  'Vegan',
  'Lactose-free',
  'Dairy-free',
  'Gluten-free',
  'Nut-free',
  'Egg-free',
  'Pork-free',
  'Other',
];

// SEND Code of Practice statuses.
export const SEN_STATUSES = [
  { value: 'none', label: 'No identified SEN' },
  { value: 'sen_support', label: 'SEN Support' },
  { value: 'ehcp', label: 'EHC Plan (EHCP)' },
];

// Common SEND categories. Codes are stored; labels are shown in chips.
export const SEN_CATEGORY_OPTIONS = [
  { code: 'ADHD', label: 'ADHD' },
  { code: 'ASD', label: 'Autism (ASD)' },
  { code: 'SLCN', label: 'Speech, Language & Communication' },
  { code: 'SEMH', label: 'Social, Emotional & Mental Health' },
  { code: 'MLD', label: 'Moderate Learning Difficulty' },
  { code: 'SLD', label: 'Severe Learning Difficulty' },
  { code: 'PD', label: 'Physical Disability' },
  { code: 'VI', label: 'Visual Impairment' },
  { code: 'HI', label: 'Hearing Impairment' },
  { code: 'DYSLEXIA', label: 'Dyslexia' },
  { code: 'DYSPRAXIA', label: 'Dyspraxia' },
  { code: 'OTHER', label: 'Other' },
];

export const EMPTY_SPECIAL_NEEDS = {
  has_sen: false,
  sen_status: 'none',
  categories: [],
  ehcp_review_date: null,
  notes: '',
};

/**
 * Coerce whatever the API gives us (null, partial object, legacy field) into
 * a complete SpecialNeeds shape so React form state stays predictable.
 */
export function normalizeSpecialNeeds(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SPECIAL_NEEDS };
  return {
    has_sen: Boolean(raw.has_sen),
    sen_status: raw.sen_status || 'none',
    categories: Array.isArray(raw.categories) ? raw.categories : [],
    ehcp_review_date: raw.ehcp_review_date || null,
    notes: raw.notes || '',
  };
}

/**
 * Returns the human-readable label for a SEN status code.
 */
export function senStatusLabel(code) {
  const found = SEN_STATUSES.find((s) => s.value === code);
  return found ? found.label : code;
}

/**
 * Returns the human-readable label for a SEN category code, or the code
 * itself if unknown (e.g. an org has stored a custom value).
 */
export function senCategoryLabel(code) {
  const found = SEN_CATEGORY_OPTIONS.find((c) => c.code === code);
  return found ? found.label : code;
}
